import type React from 'react';
import { createElement } from 'react';
import Pill from '@/app/common/components/Pill/index.tsx';
import type { FiltersGroup, FiltersGroups } from '@/app/common/filters/core/filtersTypes.ts';
import type { HandleFilterChanged } from '@/app/common/filters/core/filterComponentType.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { FilterGroupBadges } from '@/app/common/filters/surfaces/badges/FilterGroupBadges.tsx';
import { hasActiveBadges } from '@/app/common/filters/surfaces/badges/FilterGroupsBadges.tsx';
import type { CustomFilterLabelCreators } from '@/app/common/filters/surfaces/forms/FilterGroupsComponents.tsx';
import { getSmartQuickFilters } from '@/app/common/filters/quickFilters/personaQuickFilters.ts';
import type { FilterFlags } from '@/app/common/filters/core/filterFlags.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import type { QuickFilter } from '@/app/common/filters/quickFilters/quickFilterCatalogue.ts';
import type { FilterSessionValue } from '@/app/common/filters/session/sessionState.ts';
import type { ResolvedQuickFilter } from '@/app/common/filters/quickFilters/resolveQuickFilters.ts';
import { resolveQuickFilters } from '@/app/common/filters/quickFilters/resolveQuickFilters.ts';
import { chipBehaviourOf } from '@/app/common/filters/types/filterTypeBehaviour.ts';
import { isQuickFilterApplied } from '@/app/common/filters/quickFilters/quickFilterSelection.ts';
import { PillRow } from '@/app/common/filters/surfaces/styled.ts';
import { FILTER_BADGE_LABEL_CREATORS } from '@/app/common/filters/surfaces/labels/filtersLabelsUtils.tsx';
import { selectionLabelForGroup } from '@/app/common/filters/surfaces/labels/selectionLabels.tsx';
import { withSelectedValues } from '@/app/common/filters/core/filterOptionSelection.ts';
import { highlightedTracking } from '@/app/common/filters/core/filterTracking.ts';
import {
  RESULT_PAGE_FILTER_TRIGGER_SECTION,
  RESULT_PAGE_FILTER_TYPE,
} from '@/app/common/starTrack/events/resultPageFilter.ts';

type FilterChip = {
  key: string;
  label: string;
  labelNode?: React.ReactElement;
  selected: boolean;
  disabled: boolean;
  apply: () => void;
};

type FilterChipsSectionProps = {
  chips: FilterChip[];
  disabled: boolean;
  labelledBy?: string;
};

export const FilterChipsSection = ({ chips, disabled, labelledBy }: FilterChipsSectionProps) => {
  if (!chips.length) return null;

  return (
    <PillRow role="group" aria-labelledby={labelledBy}>
      {chips.map((chip) => (
        <Pill
          key={chip.key}
          isLoading={false}
          busy={disabled || chip.disabled}
          isOpen={false}
          hasPopover={false}
          selected={chip.selected}
          label={chip.labelNode ?? chip.label}
          handleClick={chip.apply}
        />
      ))}
    </PillRow>
  );
};

type SelectedFiltersSectionProps = {
  filtersGroups: FiltersGroups;
  customFilterLabelCreators: CustomFilterLabelCreators;
  isLoading: boolean;
  handleFilterChanged: HandleFilterChanged;
  labelledBy?: string;
};

export const SelectedFiltersSection = ({
  filtersGroups,
  customFilterLabelCreators,
  isLoading,
  handleFilterChanged,
  labelledBy,
}: SelectedFiltersSectionProps) => {
  if (!hasActiveBadges(filtersGroups)) return null;

  return (
    <PillRow role="group" aria-labelledby={labelledBy}>
      {filtersGroups.map((filtersGroup) => (
        <FilterGroupBadges
          key={filtersGroup.id}
          filtersGroup={filtersGroup as FiltersGroup}
          customFilterLabelCreators={customFilterLabelCreators}
          isLoading={isLoading}
          handleFilterChanged={handleFilterChanged}
        />
      ))}
    </PillRow>
  );
};

// A chip describes the values it remembers, which need not match the live selection.
const groupShowing = (group: FiltersGroup, values: string[]): FiltersGroup => ({
  ...group,
  filters: group.filters.map((filter) => withSelectedValues(filter, values)),
});

const chipLabelNode = (resolved: ResolvedQuickFilter, filtersGroups: FiltersGroups): React.ReactElement | undefined => {
  const group = filtersGroups.find((candidate) => candidate.id === resolved.groupId) as FiltersGroup | undefined;
  const selectionLabel = group && selectionLabelForGroup(group);
  if (group && selectionLabel) return <>{createElement(selectionLabel, groupShowing(group, resolved.values))}</>;

  const createLabel = FILTER_BADGE_LABEL_CREATORS[resolved.filter.name];
  if (resolved.translationKey || !createLabel) return undefined;

  return <>{createLabel(resolved.option)}</>;
};

const toChip = (
  resolved: ResolvedQuickFilter,
  handleFilterChanged: HandleFilterChanged,
  filtersGroups: FiltersGroups,
  filterType: RESULT_PAGE_FILTER_TYPE
): FilterChip => {
  const selected = isQuickFilterApplied(resolved.filter, resolved.values);

  return {
    key: resolved.id,
    label: resolved.label,
    labelNode: chipLabelNode(resolved, filtersGroups),
    selected,
    disabled: !resolved.available && !selected,
    apply: () =>
      handleFilterChanged(
        [resolved.chips.toggle(resolved.filter, resolved.values)],
        undefined,
        highlightedTracking(RESULT_PAGE_FILTER_TRIGGER_SECTION.POP_UP, resolved.filter, filterType)
      ),
  };
};

const toQuickFilters = (filtersGroups: FiltersGroups, chips: FilterSessionValue[]): QuickFilter[] => {
  const resolved = chips.flatMap(({ groupId, value }) => {
    const group = filtersGroups.find((candidate) => candidate.id === groupId && 'filters' in candidate) as
      FiltersGroup | undefined;
    const filter = group?.filters.find((candidate) =>
      chipBehaviourOf(candidate)
        ?.options(candidate)
        .some((option) => option.value === value)
    );

    if (!group || !filter) return [];
    return [{ id: `${groupId}:${value}`, groupId: group.id, targets: [{ filterName: filter.name, values: [value] }] }];
  });

  const withValuesOf = (into: QuickFilter, from: QuickFilter): QuickFilter => ({
    ...into,
    targets: [{ ...into.targets[0], values: [...into.targets[0].values, ...from.targets[0].values] }],
  });

  // Groups that describe a whole selection get one chip for everything remembered of them.
  const collapses = (groupId: string) => {
    const group = filtersGroups.find((candidate) => candidate.id === groupId) as FiltersGroup | undefined;
    return !!group && !!selectionLabelForGroup(group);
  };

  return resolved.reduce<QuickFilter[]>((chipsSoFar, chip) => {
    if (!collapses(chip.groupId)) return [...chipsSoFar, chip];

    const existing = chipsSoFar.find((candidate) => candidate.groupId === chip.groupId);
    if (!existing) return [...chipsSoFar, { ...chip, id: chip.groupId }];

    return chipsSoFar.map((candidate) => (candidate === existing ? withValuesOf(candidate, chip) : candidate));
  }, []);
};

type ModalChipSections = {
  draftFiltersGroups: FiltersGroups;
  productsData?: ProductsData;
  searchParams: SearchParams;
  handleFilterChanged: HandleFilterChanged;
  recentlyUsedChips: FilterSessionValue[];
  config?: FilterFlags;
};

export const useModalChipSections = ({
  draftFiltersGroups,
  productsData,
  searchParams,
  handleFilterChanged,
  recentlyUsedChips,
  config,
}: ModalChipSections) => {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();

  const toChips = (quickFilters: QuickFilter[], filterType: RESULT_PAGE_FILTER_TYPE) =>
    resolveQuickFilters({ filtersGroups: draftFiltersGroups, t, lang: localization.lang, quickFilters }).map(
      (resolved) => toChip(resolved, handleFilterChanged, draftFiltersGroups, filterType)
    );

  return {
    recentlyUsed: toChips(toQuickFilters(draftFiltersGroups, recentlyUsedChips), RESULT_PAGE_FILTER_TYPE.RECENT),
    popular: toChips(getSmartQuickFilters({ config, searchParams, productsData }), RESULT_PAGE_FILTER_TYPE.POPULAR),
  };
};
